import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Upload, Download, Trash2, Loader2, Paperclip, FileText, Image as ImageIcon } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-03-01 (Dave) — Element / Association Attachments modal.
// Allowed types: JPEG (.jpg/.jpeg), PNG (.png), PDF (.pdf),
//   Word (.doc / .docx), Excel (.xls / .xlsx), CSV (.csv).
// Rules: 15 MB per file, 10 files per parent. Writers can upload +
// delete; view-only can list + download (backend gate).
const ALLOWED_EXT = /\.(jpe?g|png|pdf|docx?|xlsx?|csv)$/i;
const MAX_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function AttachmentsModal({ element, association, canWrite, onClose, onChanged }) {
  // 2026-03-01 (Dave) — Modal is now polymorphic: it binds to either an
  // `element` (Elements grid paperclip) or an `association` (Association
  // tab header paperclip). Exactly one of the two props should be set.
  const isAssociation = !!association;
  const resource = isAssociation ? association : element;
  const parentId = isAssociation ? resource.aid : resource.element_id;
  const parentLabel = isAssociation ? "association" : "element";
  const listUrl = isAssociation
    ? `${API}/associations/${parentId}/attachments`
    : `${API}/elements/${parentId}/attachments`;
  const uploadUrl = listUrl; // POST same path
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // 2026-03-01 (Dave) — Per-file upload progress rows for multi-file drops.
  // Shape: [{ id, name, size, status: "queued"|"uploading"|"done"|"error", percent, error }]
  const [uploadProgress, setUploadProgress] = useState([]);
  const dragDepthRef = useRef(0); // reliable enter/leave counting across children
  const fileInputRef = useRef(null);

  const fetchList = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(listUrl, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load attachments.");
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handlePick = () => {
    // 2026-03-04 (Dave bug) — After a FAILED upload, re-picking the SAME
    // file from the native picker would silently no-op because the input
    // still held that file as its value (browsers de-dupe onChange when
    // the selected file hasn't changed). `handleUpload` clears the value
    // AFTER a pick, but a pick that never happens (user cancels) or a
    // race where the picker opens against a still-populated input can
    // leave it stale. Belt-and-suspenders: wipe the value the moment the
    // user asks for the picker, so any subsequent selection is treated
    // as "new".
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  // 2026-03-01 (Dave) — Multi-file uploader. Runs files SERIALLY so we
  // respect the per-element cap accurately (each POST is atomic on the
  // backend; another parallel POST could sneak past the count check).
  // Every file gets its own progress row that stays visible until manually
  // dismissed OR until all rows are "done" (which auto-dismisses after
  // 2.5s so the user sees the "check" state).
  const uploadFiles = useCallback(async (rawFiles) => {
    const files = Array.from(rawFiles || []).filter(Boolean);
    if (files.length === 0) return;
    setError(null);
    // 2026-03-04 (Dave bug) — Reset drag state on every upload attempt.
    // If a prior drop-then-fail cycle bailed out before the leave/end
    // handlers ran, `dragDepthRef` could stay > 0 and `isDragging` could
    // stay true, which pins the drop overlay on-screen AND makes every
    // subsequent `handleDrop` short-circuit on `uploading` if it fires
    // during the (very brief) window when `uploading=true` is still in
    // React's queue. Explicit reset guarantees a clean slate.
    dragDepthRef.current = 0;
    setIsDragging(false);

    // Pre-validate ALL files up front. Anything that fails gets a "queued"
    // row that immediately flips to "error" so the user sees which ones
    // were rejected and why.
    const entries = files.map((f) => {
      let err = null;
      if (!ALLOWED_EXT.test(f.name)) err = "Not a JPEG, PNG, PDF, Word, Excel, or CSV.";
      else if (f.size > MAX_BYTES) err = `Exceeds 15 MB (${humanSize(f.size)}).`;
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${f.name}`,
        name: f.name,
        size: f.size,
        file: f,
        status: err ? "error" : "queued",
        percent: 0,
        error: err,
      };
    });

    setUploadProgress((prev) => [...prev, ...entries]);
    setUploading(true);
    try {
      let liveCount = items.length;
      for (const entry of entries) {
        if (entry.status === "error") continue;
        if (liveCount >= MAX_FILES) {
          setUploadProgress((prev) => prev.map((p) => (
            p.id === entry.id
              ? { ...p, status: "error", error: `Element already at ${MAX_FILES}-file cap.` }
              : p
          )));
          continue;
        }
        // Mark uploading.
        setUploadProgress((prev) => prev.map((p) => (
          p.id === entry.id ? { ...p, status: "uploading" } : p
        )));
        const fd = new FormData();
        fd.append("file", entry.file);
        try {
          await axios.post(uploadUrl, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (!evt.total) return;
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setUploadProgress((prev) => prev.map((p) => (
                p.id === entry.id ? { ...p, percent: pct } : p
              )));
            },
          });
          setUploadProgress((prev) => prev.map((p) => (
            p.id === entry.id ? { ...p, status: "done", percent: 100 } : p
          )));
          liveCount += 1;
        } catch (e) {
          const msg = e?.response?.data?.detail || e?.message || "Upload failed.";
          setUploadProgress((prev) => prev.map((p) => (
            p.id === entry.id ? { ...p, status: "error", error: msg } : p
          )));
        }
      }
      await fetchList();
      onChanged?.();
    } finally {
      setUploading(false);
      // Auto-dismiss the progress rows that landed cleanly after 2.5s.
      // Error rows stay until the user closes them so the reason is visible.
      setTimeout(() => {
        setUploadProgress((prev) => prev.filter((p) => p.status !== "done"));
      }, 2500);
    }
  }, [uploadUrl, items.length, fetchList, onChanged]);

  const dismissProgress = (id) => {
    setUploadProgress((prev) => prev.filter((p) => p.id !== id));
  };

  const handleUpload = (evt) => {
    // 2026-03-03 (Dave bug) — `evt.target.files` is a LIVE FileList tied to
    // the input element. Clearing `evt.target.value` (so the same file can
    // be re-picked later) also empties `.files`, so the previous call
    // order `const files = evt.target.files; evt.target.value = "";`
    // shipped an empty FileList to `uploadFiles` — the picker silently
    // did nothing while drag-and-drop (which reads from `dataTransfer`)
    // still worked. Snapshot into a plain Array before clearing.
    const files = Array.from(evt.target.files || []);
    evt.target.value = "";
    uploadFiles(files);
  };

  // ── Drag-and-drop ────────────────────────────────────────────────
  // Use a depth counter so overlapping child elements don't cause
  // rapid enter/leave flicker on the visual highlight.
  const handleDragEnter = (e) => {
    if (!canWrite || uploading) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) setIsDragging(true);
  };
  const handleDragOver = (e) => {
    if (!canWrite || uploading) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (e) => {
    if (!canWrite || uploading) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };
  const handleDrop = (e) => {
    if (!canWrite || uploading) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) uploadFiles(files);
  };

  const handleDownload = (att) => {
    // Open in new tab — inline PDF/JPEG previews, cookie auth carries.
    window.open(`${API}/attachments/${att.attachment_id}/download`, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.attachment_id;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/attachments/${id}`, { withCredentials: true });
      setDeleteConfirm(null);
      await fetchList();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
      throw e;
    } finally {
      setDeletingId(null);
    }
  };

  const canAddMore = canWrite && items.length < MAX_FILES && !uploading;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ background: "rgba(15,42,68,0.55)" }}
        onClick={onClose}
      >
        <div
          className="bg-white rounded-[8px] border-2 border-navy shadow-2xl w-[640px] max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="attachments-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-rule">
            <div className="flex items-center gap-2">
              <Paperclip size={16} className="text-navy" />
              <div>
                <div className="text-navy font-semibold text-[14px] leading-tight">Attachments</div>
                <div className="text-slate text-[11px] truncate max-w-[440px]">{resource.name}</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate hover:text-navy"
              title="Close"
              data-testid="attachments-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — drag-and-drop zone */}
          <div
            className="flex-1 overflow-auto px-5 py-3 relative"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="attachments-dropzone"
          >
            {isDragging && (
              <div
                className="absolute inset-2 z-10 flex items-center justify-center rounded-[6px] border-2 border-dashed border-navy bg-navy-wash/70 pointer-events-none"
                data-testid="attachments-drop-overlay"
              >
                <div className="flex flex-col items-center gap-1 text-navy">
                  <Upload size={22} />
                  <div className="text-[12px] font-semibold">Drop to upload</div>
                  <div className="text-[10px] text-slate">JPEG · PNG · PDF · Word · Excel · CSV · max 15 MB · multiple files OK</div>
                </div>
              </div>
            )}

            {/* Per-file upload progress rows (multi-file drop / picker).
                Kept above the list so the eye lands on active work first. */}
            {uploadProgress.length > 0 && (
              <ul
                className="mb-3 space-y-1.5 border border-rule rounded-[4px] bg-navy-wash/30 p-2"
                data-testid="attachments-progress-list"
              >
                {uploadProgress.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-2"
                    data-testid={`attachments-progress-${p.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] text-navy truncate">{p.name}</div>
                        <div className="text-[10px] text-slate whitespace-nowrap">
                          {p.status === "done" ? "Done"
                            : p.status === "error" ? "Failed"
                            : p.status === "uploading" ? `${p.percent}%`
                            : "Queued"}
                          {" · "}{humanSize(p.size)}
                        </div>
                      </div>
                      <div className="mt-0.5 h-[3px] rounded-[2px] bg-white overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            p.status === "error" ? "bg-coral"
                              : p.status === "done" ? "bg-navy"
                              : "bg-navy/70"
                          }`}
                          style={{ width: `${p.status === "error" ? 100 : p.percent}%` }}
                        />
                      </div>
                      {p.status === "error" && p.error && (
                        <div className="text-[10px] text-coral mt-0.5">{p.error}</div>
                      )}
                    </div>
                    <button
                      onClick={() => dismissProgress(p.id)}
                      className="text-slate hover:text-navy p-0.5"
                      title="Dismiss"
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate">
                <Loader2 size={16} className="animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-slate text-[12px]">
                No attachments yet.{" "}
                {canWrite
                  ? "Drop one or more JPEG, PNG, PDF, Word, Excel, or CSV files here — or use the Upload button below (max 15 MB each)."
                  : "Only writers can upload files."}
              </div>
            ) : (
              <ul className="divide-y divide-rule" data-testid="attachments-list">
                {items.map((att) => {
                  const isPdf = att.content_type === "application/pdf";
                  const isImage = (att.content_type || "").startsWith("image/");
                  const Icon = isPdf ? FileText : isImage ? ImageIcon : FileText;
                  return (
                    <li
                      key={att.attachment_id}
                      className="flex items-center gap-3 py-2"
                      data-testid={`attachment-${att.attachment_id}`}
                    >
                      <Icon size={16} className={isPdf ? "text-coral" : "text-navy"} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-navy truncate">{att.filename}</div>
                        <div className="text-[10px] text-slate">
                          {humanSize(att.size_bytes)} · {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" }) : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(att)}
                        className="text-slate hover:text-navy p-1"
                        title="Download / preview"
                        data-testid={`attachment-download-${att.attachment_id}`}
                      >
                        <Download size={14} />
                      </button>
                      {canWrite && (
                        <button
                          onClick={() => setDeleteConfirm(att)}
                          disabled={deletingId === att.attachment_id}
                          className="text-slate hover:text-coral p-1 disabled:opacity-40"
                          title="Delete"
                          data-testid={`attachment-delete-${att.attachment_id}`}
                        >
                          {deletingId === att.attachment_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {error && (
              <div className="mt-3 rounded-[4px] bg-coral/10 border border-coral text-coral text-[11px] px-3 py-2" data-testid="attachments-error">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-rule bg-navy-wash/40">
            <div className="text-[11px] text-slate">
              {items.length} / {MAX_FILES} files · JPEG · PNG · PDF · Word · Excel · CSV · max 15 MB each
            </div>
            {canWrite ? (
              <button
                onClick={handlePick}
                disabled={!canAddMore}
                className="inline-flex items-center gap-1.5 bg-navy text-white text-[12px] font-semibold px-3 py-1.5 rounded-[4px] hover:bg-navy/90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="attachments-upload-btn"
              >
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {uploading ? "Uploading…" : "Upload files"}
              </button>
            ) : (
              <div className="text-[11px] text-slate italic">Read-only</div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/csv"
              multiple
              onChange={handleUpload}
              className="hidden"
              data-testid="attachments-file-input"
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete this attachment?"
        message={
          deleteConfirm ? (
            <>
              <span className="font-semibold">&ldquo;{deleteConfirm.filename}&rdquo;</span>{" "}
              will be permanently removed from this element.
            </>
          ) : ""
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
        testid="attachment-delete-confirm"
      />
    </>
  );
}
